// Shared hard-recovery helpers for mobile simulation entities.
//
// Dynamic blockers may appear while an entity occupies their footprint (a wall finishes,
// a gate closes, etc.). Recovery is deliberately side-preserving: the entity may leave the
// new blocker toward its last open position, but may never be ejected through permanent map
// terrain or out the far side of a closed gate.

import type { PlayerId } from '../content/types';
import { dist, type Vec2 } from '../shared/vec';
import { segmentClear } from './nav';
import type { Sim } from './sim';

/** Keep recovery anchors away from narrow gate throats, where a later close would cover them. */
const OPEN_CLEARANCE = 2.1;

export function hasOpenClearance(sim: Sim, p: Vec2, owner: PlayerId): boolean {
  if (!sim.nav.isWalkable(p, owner)) return false;
  for (let i = 0; i < 16; i++) {
    const angle = (i / 16) * Math.PI * 2;
    const probe = {
      x: p.x + Math.cos(angle) * OPEN_CLEARANCE,
      y: p.y + Math.sin(angle) * OPEN_CLEARANCE,
    };
    if (!sim.nav.isWalkable(probe, owner)) return false;
  }
  return true;
}

/** Physical segment probe. Unlike planning LoS, this never treats the actor's shut owned gate as open. */
export function physicalLineClear(sim: Sim, a: Vec2, b: Vec2, owner: PlayerId): boolean {
  return segmentClear(a, b, (cx, cy) => sim.nav.isWalkableCell(cx, cy, owner));
}

/** Physical line probe widened to the actor's body; used by forced straight-line dashes. */
export function physicalCorridorClear(
  sim: Sim,
  a: Vec2,
  b: Vec2,
  owner: PlayerId,
  radius: number,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length < 0.1) return true;
  const px = -dy / length;
  const py = dx / length;
  for (const offset of [-radius, 0, radius]) {
    if (!physicalLineClear(
      sim,
      { x: a.x + px * offset, y: a.y + py * offset },
      { x: b.x + px * offset, y: b.y + py * offset },
      owner,
    )) return false;
  }
  return true;
}

/** Segment probe against immutable terrain only; dynamic walls/gates are intentionally ignored. */
function terrainLineClear(sim: Sim, a: Vec2, b: Vec2): boolean {
  return segmentClear(a, b, (cx, cy) => sim.nav.isTerrainWalkableCell(cx, cy));
}

/**
 * Find the nearest physical cell on the last-open side of a dynamic blocker. The half-plane
 * restriction is the important invariant: recovery can back out of a gate that closed over a
 * unit, but can never pop the unit through to the opposite side.
 */
export function dynamicEjectionPoint(
  sim: Sim,
  p: Vec2,
  lastOpen: Vec2,
  owner: PlayerId,
  maxRadius = 12,
): Vec2 | null {
  if (!sim.nav.isTerrainWalkable(p)) return null;
  let ax = lastOpen.x - p.x;
  let ay = lastOpen.y - p.y;
  let alen = Math.hypot(ax, ay);
  if (alen < 0.1) {
    // A structure can be placed over an entity before it ever records a second anchor.
    // Home/core side is the deterministic safe fallback and also preserves gate ownership.
    const home = sim.map.corePositions[owner];
    ax = home.x - p.x;
    ay = home.y - p.y;
    alen = Math.hypot(ax, ay);
    if (alen < 0.1) {
      ax = owner === 0 ? -1 : 1;
      ay = 0;
      alen = 1;
    }
  }
  const ux = ax / alen;
  const uy = ay / alen;
  const cx = Math.floor(p.x);
  const cy = Math.floor(p.y);
  let best: Vec2 | null = null;
  let bestScore = Infinity;
  let bestCell = Infinity;

  for (let radius = 1; radius <= maxRadius; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
        const candidate = { x: cx + dx + 0.5, y: cy + dy + 0.5 };
        if (!sim.nav.isWalkable(candidate, owner)) continue;
        const vx = candidate.x - p.x;
        const vy = candidate.y - p.y;
        const towardEntrySide = vx * ux + vy * uy;
        if (towardEntrySide < -0.05) continue;
        // A straight recovery snap is only legal when it crosses no immutable terrain.
        if (!terrainLineClear(sim, p, candidate)) continue;
        const cell = (cy + dy) * sim.nav.w + cx + dx;
        const score = Math.hypot(vx, vy) + dist(candidate, lastOpen) * 0.02 - towardEntrySide * 0.01;
        if (score < bestScore - 1e-9 || (Math.abs(score - bestScore) <= 1e-9 && cell < bestCell)) {
          best = candidate;
          bestScore = score;
          bestCell = cell;
        }
      }
    }
    // The first two rings beyond the first valid point are enough to find a better same-side
    // center without considering a visibly long snap.
    if (best && radius >= Math.ceil(bestScore) + 2) break;
  }
  return best;
}

/** Deterministic local yield used after real net stalls (not merely a stale path rebuild). */
export function yieldWaypoint(
  sim: Sim,
  id: number,
  p: Vec2,
  goal: Vec2,
  owner: PlayerId,
): Vec2 | null {
  const dx = goal.x - p.x;
  const dy = goal.y - p.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.1) return null;
  const fx = dx / len;
  const fy = dy / len;
  const px = -fy;
  const py = fx;
  const side = (id & 1) === 0 ? 1 : -1;
  const offsets = [
    { forward: 0.35, lateral: side * 2.4 },
    { forward: 0.35, lateral: -side * 2.4 },
    { forward: -2.4, lateral: side * 0.8 },
    { forward: -2.4, lateral: -side * 0.8 },
    { forward: -2.8, lateral: 0 },
  ];
  for (const offset of offsets) {
    const candidate = {
      x: p.x + fx * offset.forward + px * offset.lateral,
      y: p.y + fy * offset.forward + py * offset.lateral,
    };
    if (!sim.nav.isWalkable(candidate, owner)) continue;
    if (physicalLineClear(sim, p, candidate, owner)) return candidate;
  }
  return null;
}
